<template>
  <el-dialog
    title="非券订单自动结佣设置"
    :visible.sync="dialogVisible"
    :close-on-click-modal="false"
    :close-on-press-escape="!saveLoading"
    :before-close="handleBeforeClose"
    append-to-body
    width="510px"
    custom-class="non-coupon-auto-settle-dialog"
    @close="invalidateQuery"
    @closed="handleClosed"
  >
    <div v-loading="queryLoading" class="auto-settle-setting__content">
      <el-form
        ref="autoSettleFormRef"
        :model="form"
        :rules="rules"
        label-width="100px"
      >
        <el-form-item label="自动结佣开关">
          <div class="auto-settle-setting__row">
            <el-switch
              v-model="form.autoSettleEnabled"
              :active-value="1"
              :inactive-value="0"
              :disabled="saveLoading"
              @change="handleSwitchChange"
            />
            <span class="auto-settle-setting__hint">开启后，将会自动给团长结佣。</span>
          </div>
        </el-form-item>
        <el-form-item label="次月" prop="monthlySettleDay">
          <div class="auto-settle-setting__row">
            <el-input
              v-model.trim="form.monthlySettleDay"
              class="auto-settle-setting__day-input"
              :disabled="saveLoading"
              @input="handleDayInput"
            />
            <span>日</span>
            <span class="auto-settle-setting__hint">当月佣金次月n号1点自动结给团长。</span>
          </div>
        </el-form-item>
      </el-form>
      <div v-if="queryError" class="auto-settle-setting__query-error">
        <span>{{ queryError }}</span>
        <el-button type="text" :disabled="saveLoading" @click="fetchConfig">重新查询</el-button>
      </div>
    </div>

    <div slot="footer" class="auto-settle-setting__footer">
      <div class="auto-settle-setting__effective-tip">设置后立刻生效。</div>
      <div>
        <el-button :disabled="saveLoading" @click="handleClose">取 消</el-button>
        <el-button
          type="primary"
          :loading="saveLoading"
          :disabled="queryLoading || !configLoaded || saveLoading"
          @click="handleConfirm"
        >确 定</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import poi from '@/api/poi'

const MONTHLY_SETTLE_DAY_PATTERN = /^(?:[1-9]|[12]\d|3[01])$/
const PoiModel = new poi()

export default {
  name: 'NonCouponAutoSettleSetting',
  data() {
    const validateMonthlySettleDay = (rule, value, callback) => {
      if (this.form.autoSettleEnabled !== 1) {
        callback()
        return
      }
      if (value === '' || value == null) {
        callback(new Error('请输入自动结佣日期'))
        return
      }
      if (!MONTHLY_SETTLE_DAY_PATTERN.test(String(value))) {
        callback(new Error('请输入1-31的整数'))
        return
      }
      callback()
    }

    return {
      dialogVisible: false,
      queryLoading: false,
      saveLoading: false,
      configLoaded: false,
      queryError: '',
      queryRequestId: 0,
      form: {
        autoSettleEnabled: 0,
        monthlySettleDay: ''
      },
      rules: {
        monthlySettleDay: [
          {
            validator: validateMonthlySettleDay,
            trigger: ['blur', 'change']
          }
        ]
      }
    }
  },
  methods: {
    open() {
      if (this.dialogVisible || this.queryLoading || this.saveLoading) return
      this.resetState()
      this.dialogVisible = true
      this.fetchConfig()
    },
    async fetchConfig() {
      if (this.queryLoading || this.saveLoading || !this.dialogVisible) return
      const requestId = ++this.queryRequestId
      this.queryLoading = true
      this.configLoaded = false
      this.queryError = ''
      try {
        const res = await PoiModel.getNonCouponAutoSettleConfig({})
        if (!this.isCurrentQuery(requestId)) return
        if (!res || !res.data || res.data.code !== 200) {
          const message = res && res.data && res.data.message
            ? res.data.message
            : '获取自动结佣配置失败'
          this.queryError = message
          this.$message.error(message)
          return
        }
        const data = res.data.data && typeof res.data.data === 'object'
          ? res.data.data
          : {}
        const autoSettleEnabled = data.nonCouponAutoSettle
        if (autoSettleEnabled != null && autoSettleEnabled !== 0 && autoSettleEnabled !== 1) {
          const message = '自动结佣配置数据异常，请重新查询'
          this.queryError = message
          this.$message.error(message)
          return
        }
        this.form.autoSettleEnabled = data.nonCouponAutoSettle === 1 ? 1 : 0
        this.form.monthlySettleDay = this.form.autoSettleEnabled === 1 && data.nonCouponAutoSettleDate != null
          ? String(data.nonCouponAutoSettleDate)
          : ''
        this.configLoaded = true
        this.$nextTick(() => {
          this.$refs.autoSettleFormRef && this.$refs.autoSettleFormRef.clearValidate()
        })
      } catch (error) {
        if (!this.isCurrentQuery(requestId)) return
        const message = this.getErrorMessage(error, '获取自动结佣配置失败')
        this.queryError = message
        this.$message.error(message)
      } finally {
        if (requestId === this.queryRequestId) {
          this.queryLoading = false
        }
      }
    },
    isCurrentQuery(requestId) {
      return requestId === this.queryRequestId && this.dialogVisible
    },
    getErrorMessage(error, fallback) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : error && error.message
          ? error.message
          : fallback
    },
    handleBeforeClose(done) {
      if (this.saveLoading) return
      done()
    },
    invalidateQuery() {
      this.queryRequestId += 1
      this.queryLoading = false
    },
    handleSwitchChange() {
      if (this.form.autoSettleEnabled !== 1) {
        this.$refs.autoSettleFormRef && this.$refs.autoSettleFormRef.clearValidate('monthlySettleDay')
      }
    },
    handleDayInput(value) {
      this.form.monthlySettleDay = String(value).replace(/\D/g, '')
    },
    handleConfirm() {
      if (this.saveLoading || this.queryLoading || !this.configLoaded) return
      this.saveLoading = true
      this.$refs.autoSettleFormRef.validate(valid => {
        if (!valid) {
          this.saveLoading = false
          return
        }
        this.saveConfig()
      })
    },
    async saveConfig() {
      const payload = {
        nonCouponAutoSettle: this.form.autoSettleEnabled
      }
      if (this.form.autoSettleEnabled === 1) {
        payload.nonCouponAutoSettleDate = Number(this.form.monthlySettleDay)
      }
      try {
        const res = await PoiModel.saveNonCouponAutoSettleConfig(payload)
        if (!res || !res.data || res.data.code !== 200) {
          this.$message.error(res && res.data && res.data.message ? res.data.message : '设置自动结佣配置失败')
          return
        }
        this.$message.success(res.data.message || '设置成功')
        this.dialogVisible = false
        this.$emit('success')
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '设置自动结佣配置失败'))
      } finally {
        this.saveLoading = false
      }
    },
    handleClose() {
      if (this.saveLoading) return
      this.dialogVisible = false
    },
    handleClosed() {
      this.resetState()
    },
    resetState() {
      this.invalidateQuery()
      this.dialogVisible = false
      this.queryLoading = false
      this.saveLoading = false
      this.configLoaded = false
      this.queryError = ''
      this.form.autoSettleEnabled = 0
      this.form.monthlySettleDay = ''
      this.$refs.autoSettleFormRef && this.$refs.autoSettleFormRef.clearValidate()
    }
  }
}
</script>

<style scoped lang="less">
.auto-settle-setting {
  &__content {
    min-height: 120px;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 40px;
  }

  &__day-input {
    width: 100px;
  }

  &__hint {
    color: #a8abb2;
    line-height: 20px;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__effective-tip {
    color: #606266;
    line-height: 20px;
  }

  &__query-error {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-left: 100px;
    color: #f56c6c;
    line-height: 20px;
  }
}

/deep/ .non-coupon-auto-settle-dialog {
  max-width: calc(100vw - 32px);

  .el-dialog__body {
    max-height: calc(100vh - 180px);
    overflow-y: auto;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/pc/miniProgram/poiCps/components/commissioSetting/nonCouponAutoSettleSetting.vue